import { cartFreeItemRememberParameters } from '../../helpers/FreeItemCookies';

const cartTriggerSelector = '.twc-hdr-cart';
const cartCountSelector = '.twc-hdr-cart-total-count';

let appMainReady = null;
let appMain = null;
let cartTogglePending = false;

/**
 * Returns the cart quantity available in the server-rendered bootstrap data.
 * This mirrors CartV3's count by excluding Redo service lines.
 */
export const getBootstrapCartCount = () => {
  const items = window.cartDataJson?.items;

  if (Array.isArray(items)) {
    return items.reduce((count, item) => {
      if (item?.vendor === 're:do') {
        return count;
      }

      return count + Number(item?.quantity || 0);
    }, 0);
  }

  return Number(
    window.cartBootstrap?.item_count || window.cartDataJson?.item_count || 0
  );
};

/**
 * Paints the header badge without loading React, Redux, or CartV3.
 */
export const updateBootstrapCartCount = () => {
  const count = getBootstrapCartCount();

  document.querySelectorAll(cartCountSelector).forEach((counter) => {
    counter.setAttribute('data-count', count);
    counter.textContent = count;
  });
};

/**
 * Persists parameter-based GWP activation before the lazy CartV3 bundle loads.
 * This keeps landing-page and quick-cart journeys equivalent to full CartV3.
 */
export const rememberBootstrapFreeItemParameters = () => {
  const configuredItems = window.cartDataJson?.free_item_data?.byParameter;
  const legacyItems = window.cartData?.parameters;

  return cartFreeItemRememberParameters(
    Array.isArray(configuredItems)
      ? configuredItems
      : Array.isArray(legacyItems)
        ? legacyItems
        : []
  );
};

/**
 * Loads the storefront React application once. CartV3 remains available on
 * every route, but non-product pages do not download it until cart intent.
 */
export const loadAppMain = () => {
  if (!appMainReady) {
    if (!document.querySelector('#app')) {
      return Promise.reject(new Error('AppMain root is unavailable'));
    }

    appMainReady = import(
      /* webpackChunkName: "AppMain_CartV3" */
      /* webpackPrefetch: true */
      './components/App/App'
    )
      .then((module) => {
        appMain = module;
        return module;
      })
      .catch((error) => {
        appMainReady = null;
        throw error;
      });
  }

  return appMainReady;
};

const getCartTrigger = (event) =>
  event.target?.closest?.(cartTriggerSelector) || null;

/**
 * Resolves a non-empty cart URL even when the trigger is not an anchor.
 */
export const getCartFallbackUrl = (trigger) => {
  const candidates = [
    trigger?.getAttribute?.('href'),
    trigger?.href,
    window.cartBootstrap?.cart_url,
    '/cart',
  ];

  return candidates.find(
    (candidate) =>
      typeof candidate === 'string' &&
      candidate.trim().length > 0 &&
      candidate.trim() !== '#'
  );
};

const getCartShellElements = () => {
  const wrapper = document.querySelector('[data-cart-card-wrapper]');
  const card = document.querySelector('[data-cart-card]');

  return wrapper && card ? { wrapper, card } : null;
};

export const isCartShellOpen = () =>
  document.querySelector('[data-cart-card][data-state="open"]') !== null;

/**
 * Mirrors the DOM half of CartV3's `cartToggle` so the drawer paints before
 * the React chunk downloads and evaluates. CartV3 drives the same attributes
 * (and the shared `cartCloseTimer` element field) once it has loaded.
 */
export const setCartShellState = (open) => {
  const shell = getCartShellElements();

  if (!shell) {
    return false;
  }

  const { wrapper, card } = shell;

  if (open) {
    document.documentElement.setAttribute('data-cart-scroll-lock', 'active');
  } else {
    document.documentElement.removeAttribute('data-cart-scroll-lock');
  }

  if (window.matchMedia('(max-width: 767px)').matches) {
    document.body.classList.toggle('overscroll-y-contain', open);
    document.body.classList.toggle('touch-manipulation', open);
    document
      .querySelector('.twc-hdr-bottom')
      ?.classList.toggle('max-xl:invisible', open);
  }

  card.dataset.state = open ? 'open' : 'closed';
  window.clearTimeout(wrapper.cartCloseTimer);

  if (open) {
    wrapper.dataset.state = 'open';
  } else {
    wrapper.cartCloseTimer = window.setTimeout(() => {
      wrapper.dataset.state = 'closed';
    }, 150);
  }

  return true;
};

/**
 * Resolves after a rendering opportunity, so the shell's paint is committed
 * before the heavy chunk evaluation can occupy the main thread.
 */
const afterNextPaint = () =>
  new Promise((resolve) => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
  });

const preloadCartOnIntent = (event) => {
  // On touch, pointerover fires within the tap itself, so preloading gains no
  // lead time and the chunk evaluation could race the shell's first paint.
  if (event.pointerType === 'touch' || !getCartTrigger(event)) {
    return;
  }

  loadAppMain().catch(() => {});
};

const toggleCartOnIntent = (event) => {
  const trigger = getCartTrigger(event);
  const fullCart = document.querySelector('#cart[data-view="full"]');

  if (!trigger || fullCart) {
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();

  if (appMain) {
    appMain.toggleCart();
    return;
  }

  if (cartTogglePending) {
    // The chunk is still loading; keep toggle semantics on the shell alone.
    setCartShellState(!isCartShellOpen());
    return;
  }

  cartTogglePending = true;
  const shellShown = setCartShellState(true);
  const paintReady = shellShown ? afterNextPaint() : Promise.resolve();

  paintReady
    .then(loadAppMain)
    .then((module) => {
      // Skip opening when the shopper closed the shell while it loaded.
      if (!shellShown || isCartShellOpen()) {
        module.toggleCart();
      }
    })
    .catch(() => {
      // Fall back to the cart page unless the shopper dismissed the shell.
      if (!shellShown || isCartShellOpen()) {
        setCartShellState(false);
        window.location.assign(getCartFallbackUrl(trigger));
      }
    })
    .finally(() => {
      cartTogglePending = false;
    });
};

/**
 * Lets the shopper dismiss the shell (backdrop or the stand-in close button)
 * before CartV3 has loaded and attached its own close handling.
 */
const bindCartShellDismissal = () => {
  const shell = getCartShellElements();

  if (!shell) {
    return;
  }

  const { wrapper } = shell;

  wrapper.addEventListener('mousedown', (event) => {
    if (!appMain && event.target === wrapper) {
      setCartShellState(false);
    }
  });
  wrapper.addEventListener('click', (event) => {
    if (!appMain && event.target?.closest?.('[data-cart-shell-close]')) {
      setCartShellState(false);
    }
  });
};

/**
 * Starts the small global bridge. Product and full-cart routes still require
 * AppMain immediately; other routes load it on hover, focus, or click.
 */
export const initializeAppMainBootstrap = () => {
  rememberBootstrapFreeItemParameters();
  updateBootstrapCartCount();
  bindCartShellDismissal();

  document.addEventListener('pointerover', preloadCartOnIntent, true);
  document.addEventListener('focusin', preloadCartOnIntent, true);
  document.addEventListener('click', toggleCartOnIntent, true);

  if (
    document.body.classList.contains('template-product') ||
    document.querySelector('#product-full, #cart[data-view="full"]')
  ) {
    loadAppMain().catch(() => {});
  }
};
